import { Timestamp } from "firebase/firestore";

/**
 * Check if two date ranges overlap
 * @param {Date} start1 
 * @param {Date} end1 
 * @param {Date} start2 
 * @param {Date} end2 
 * @returns {boolean} True if ranges overlap
 */
function dateRangesOverlap(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}

/**
 * Detect conflicts within uploaded CSV data
 * Finds same SKU with overlapping date ranges
 * @param {Array} rows - Array of parsed CSV rows with { sku, startDate, endDate }
 * @returns {Array} Array of conflict objects with row indices
 */
export function detectInternalConflicts(rows) {
  const conflicts = [];
  const skuGroups = {};

  // Group rows by SKU
  rows.forEach((row, index) => {
    const sku = String(row.sku).trim();
    if (!skuGroups[sku]) {
      skuGroups[sku] = [];
    }
    skuGroups[sku].push({ ...row, rowIndex: index });
  });

  // Check for overlaps within each SKU group
  Object.keys(skuGroups).forEach(sku => {
    const group = skuGroups[sku];
    
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const row1 = group[i];
        const row2 = group[j];

        // Both rows must have valid dates
        if (row1.startDate && row1.endDate && row2.startDate && row2.endDate) {
          if (dateRangesOverlap(row1.startDate, row1.endDate, row2.startDate, row2.endDate)) {
            conflicts.push({
              sku,
              rowIndex1: row1.rowIndex,
              rowIndex2: row2.rowIndex,
              message: `SKU ${sku}: Row ${row1.rowIndex + 2} and Row ${row2.rowIndex + 2} have overlapping dates`,
              range1: { start: row1.startDate, end: row1.endDate },
              range2: { start: row2.startDate, end: row2.endDate }
            });
          }
        }
      }
    }
  });

  return conflicts;
}

/**
 * Detect conflicts between a new promotion and existing Firebase discounts
 * @param {string} sku 
 * @param {Date} startDate 
 * @param {Date} endDate 
 * @param {Array} existingDiscounts - Array of discount objects from Firebase
 * @returns {Object} { hasConflict: boolean, conflicts: Array }
 */
export function detectFirebaseConflicts(sku, startDate, endDate, existingDiscounts) {
  if (!existingDiscounts || existingDiscounts.length === 0) {
    return { hasConflict: false, conflicts: [] };
  }

  const conflicts = [];

  existingDiscounts.forEach((discount, index) => {
    // Convert Timestamps to Dates if needed
    const existingStart = discount.startDate instanceof Timestamp 
      ? discount.startDate.toDate() 
      : discount.startDate;
    
    const existingEnd = discount.endDate instanceof Timestamp 
      ? discount.endDate.toDate() 
      : discount.endDate;

    if (dateRangesOverlap(startDate, endDate, existingStart, existingEnd)) {
      conflicts.push({
        sku,
        existingPromotion: discount.name,
        existingRange: {
          start: existingStart,
          end: existingEnd
        },
        newRange: {
          start: startDate,
          end: endDate
        },
        message: `SKU ${sku}: Overlaps with existing promotion "${discount.name}" (${existingStart.toLocaleDateString()} - ${existingEnd.toLocaleDateString()})`
      });
    }
  });

  return {
    hasConflict: conflicts.length > 0,
    conflicts
  };
}

/**
 * Validate all rows for conflicts
 * Combines internal and Firebase conflict detection
 * @param {Array} rows - Array of parsed rows with validated dates and existing discounts
 * @returns {Object} { rowConflicts: Map, internalConflicts: Array }
 */
export function validateAllConflicts(rows) {
  const rowConflicts = new Map(); // rowIndex -> array of conflict messages
  
  // 1. Internal conflicts (within CSV)
  const internalConflicts = detectInternalConflicts(rows);
  
  internalConflicts.forEach(conflict => {
    // Mark both rows as having conflicts
    [conflict.rowIndex1, conflict.rowIndex2].forEach(idx => {
      if (!rowConflicts.has(idx)) {
        rowConflicts.set(idx, []);
      }
      rowConflicts.get(idx).push(conflict.message);
    });
  });

  // 2. Firebase conflicts (against existing data)
  rows.forEach((row, index) => {
    if (row.startDate && row.endDate && row.existingDiscounts) {
      const firebaseConflict = detectFirebaseConflicts(
        row.sku,
        row.startDate,
        row.endDate,
        row.existingDiscounts
      );

      if (firebaseConflict.hasConflict) {
        if (!rowConflicts.has(index)) {
          rowConflicts.set(index, []);
        }
        firebaseConflict.conflicts.forEach(c => {
          rowConflicts.get(index).push(c.message);
        });
      }
    }
  });

  return {
    rowConflicts,
    internalConflicts,
    hasAnyConflicts: rowConflicts.size > 0
  };
}
